// ============================================================================
// MarketOverview.jsx
// Market Overview — D3-powered chart, dark-first Stocker design, mobile-ready
//
// WIRE-UP STATUS (search "WIRED" and "TODO" to find all integration points)
// ─────────────────────────────────────────────────────────────────────────
//
// WIRED (live data flowing today):
//   [1] marketSnapshotSlice  → Indexes tab: topMovers, topGainers, topLosers, mostActive
//       Source: fetchMarketOverview() → MarketSnapshotAPI.fetchAll()
//       Endpoints: /market-overview/top-movers|top-gainers|top-losers|most-active
//       Data lands in: state.marketSnapshot.{topMovers,topGainers,topLosers,mostActive}
//       How it's used: buildIndexSeriesFromSnapshots() converts snapshot arrays
//       into { date, val } indexed series keyed by tab name.
//
// TODO (requires new backend endpoint or Redux slice — see each comment):
//   [A] nasdaqIndexSlice    → Indexes tab: S&P 500, Nasdaq, Dow Jones, FTSE 100, Nikkei
//       Need: /indices/{ticker}/prices endpoint returning {date, close}[]
//       Already have: nasdaqIndexActions + nasdaqIndexAPI + nasdaqIndexSlice
//       Missing: a backend route for non-Nasdaq indices (S&P, Dow, FTSE, Nikkei)
//       OR a Polygon aggregates proxy. Wire fetchIndexPrices for COMP.INDX first.
//
//   [B] Equities tab        → US / EU / Emerging equities sector performance
//       Need: new endpoint or Polygon /v2/aggs/ticker/{ticker}/range aggregator
//       Suggested tickers: SPY (US), VGK (EU), EEM (Emerging)
//       Suggested Redux: add fetchEquitiesSeriesThunk to stockDataActions
//
//   [C] ETFs tab            → Equity, Bond, Commodity, Mixed ETF performance
//       Need: same aggregator as [B]. Tickers: VTI, BND, GLD, VSMGX
//       Can reuse the same thunk as [B] with different ticker list.
//
//   [D] Crypto tab          → Bitcoin, Ethereum, Altcoins, DeFi, Stablecoins
//       Need: Polygon crypto endpoint /v2/aggs/ticker/X:{pair}/range
//       Tickers: X:BTCUSD, X:ETHUSD — altcoin index needs custom logic or a
//       weighted basket. Stablecoins intentionally flat — can use hardcoded stub.
//
//   [E] Gold tab            → Spot gold, futures, miners, gold ETFs
//       Need: Polygon /v2/aggs/ticker/C:XAUUSD/range (spot) + GLD, GDX, GDXJ ETFs
//       Can reuse [B]/[C] thunk structure.
//
//   [F] Bonds tab           → US, EU, Emerging, Private debt
//       Need: AGG (US), VWOB (Emerging), VBTLX or TLT. EU bond index is tricky —
//       suggest IBND or a manual FRED proxy. Private debt has no real-time feed.
//
//   [G] Range filter → API params
//       Currently: range selector slices mock data client-side (months).
//       Live: when user picks 1M/3M/6M/1Y/3Y/5Y, fire a new fetch with
//       matching start/end dates. Add onRangeChange prop or dispatch inline.
//       stockDataAPI.getStockData already accepts startDate/endDate — use it.
//
// HOW TO CONSUME THIS COMPONENT:
//   Pass data={marketOverviewData} where marketOverviewData has this shape:
//
//   {
//     'Indexes': {
//       'S&P 500':  [{ date: Date, val: number }, ...],
//       'Nasdaq':   [...],
//       ...
//     },
//     'Equities': { ... },
//     ...
//   }
//
//   Any missing category/sub automatically falls back to built-in mock.
//   Wire categories incrementally — ship and improve without big-bang rewrites.
//
// PROPS:
//   data         : CategoryData | null — real data (falls back to mock when null)
//   isLoading    : boolean             — shows skeleton while fetching
//   onSubChange  : (cat, sub) => void  — fired when user switches category/sub
//   className    : string
// ============================================================================

import React, {
  useState, useEffect, useRef, useCallback, useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import { useTheme } from '../../../theme/ThemeContext';

// ── Redux actions ─────────────────────────────────────────────────────────────
import { fetchMarketOverview } from '../../../redux/stockData/marketSnapshot/marketSnapshotActions';
// TODO [A]: import { fetchIndexPrices } from '../../../redux/stockData/nasdaqIndex/nasdaqIndexActions';
// TODO [B–F]: import { fetchEquitiesSeriesThunk } from '../../../redux/stockData/nasdaq/stockDataActions';
//             (or whatever new thunk you create for multi-ticker aggregates)

// ─── STOCKER DARK PALETTE ────────────────────────────────────────────────────
const PALETTE = [
  '#3B82F6', // blue-500   — primary Stocker blue
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#A78BFA', // violet-400
];

const DARK_C = {
  bg:          '#0f172a',
  bgCard:      '#1e293b',
  bgCardHover: '#243044',
  border:      '#334155',
  borderFaint: '#1e293b',
  textPrimary: '#f1f5f9',
  textMuted:   '#94a3b8',
  textFaint:   '#475569',
  blue:        '#3B82F6',
  green:       '#10B981',
  red:         '#F87171',
  amber:       '#F59E0B',
  gridLine:    'rgba(148,163,184,0.06)',
  crosshair:   'rgba(148,163,184,0.25)',
};

// 2026-09-11: this whole file rendered through DARK_C unconditionally --
// MarketOverviewSection.jsx (the wrapper) documented that as deliberate,
// since this component had no light counterpart to switch to. LIGHT_C is
// that counterpart.
//
// CORRECTED, same day: the first version of LIGHT_C built its surfaces
// from the `cream` scale's more saturated steps (bg #F6F2FD / bgCard
// #F3EFFC) -- on their own that reads as "light violet," which was the
// intent, but stacked with this dashboard's other cards (DashboardHero,
// PillarsIntro) each applying their own violet/blue tint on top of the
// same neutral gray-50 page, the compounding effect read as a washed,
// dingy lavender wash rather than a clean white surface -- confirmed live
// via a user screenshot. `bg`/`bgCard`/`bgCardHover` now match HomePage's
// own real light-mode surface treatment (`#ffffff` page, a barely-there
// neutral tint for cards, no hue) -- color is kept for `border` and text,
// not the fill. blue/green/red/amber are still bumped a step darker/more
// saturated than their dark-mode values -- those were tuned for
// light-on-dark contrast and fail WCAG AA as small text/icons on a light
// background.
const LIGHT_C = {
  bg:          '#FFFFFF',
  bgCard:      '#F4F4F7',
  bgCardHover: '#F3EFFC',
  border:      '#D4C5F0',
  borderFaint: '#EFEBFA',
  textPrimary: '#2E1F47',
  textMuted:   '#6B5B95',
  textFaint:   '#9B8AB8',
  blue:        '#2563EB',
  green:       '#059669',
  red:         '#DC2626',
  amber:       '#B45309',
  gridLine:    'rgba(46,31,71,0.08)',
  crosshair:   'rgba(46,31,71,0.25)',
};

// ─── MOCK DATA GENERATOR (fallback for unwired categories) ───────────────────
function lcg(seed) {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

function generateSeries(seeds, vol, drift, months) {
  const rng = lcg(seeds[0] * 31 + seeds[1] * 17 + seeds[2]);
  const pts = [];
  let v = 100;
  const end = new Date(2025, 3, 1);
  for (let i = months; i >= 0; i--) {
    const d = new Date(end);
    d.setMonth(d.getMonth() - i);
    v = Math.max(10, v + (rng() - 0.46) * vol + drift);
    pts.push({ date: d, val: parseFloat(v.toFixed(2)) });
  }
  return pts;
}

// ─── CATEGORY + SUBCATEGORY STRUCTURE ────────────────────────────────────────
// DEFAULT_STRUCTURE drives:
//   1. The tab/pill labels (keys)
//   2. The mock data fallback (seeds/vol/drift)
//   3. The color assignment for each sub-series
//
// When real data is wired for a category, DEFAULT_STRUCTURE still controls
// colors and labels — only the series data (pts) is replaced by live data.
const DEFAULT_STRUCTURE = {
  // [1] WIRED — topMovers/Gainers/Losers/MostActive from marketSnapshotSlice
  // Sub-key names match what buildIndexSeriesFromSnapshots() produces below.
  // The seeds/vol/drift here are only used when the API returns nothing.
  'Indexes': {
    'Top Movers':   { seeds:[55,28,82], vol:2.8, drift:0.58, color:PALETTE[0] },
    'Top Gainers':  { seeds:[37,91,14], vol:3.8, drift:0.68, color:PALETTE[1] },
    'Top Losers':   { seeds:[62,47,33], vol:2.2, drift:0.48, color:PALETTE[2] },
    'Most Active':  { seeds:[19,74,56], vol:2.0, drift:0.30, color:PALETTE[3] },
    // [WIRED, #14] S&P 500/Dow Jones/Volatility via real ETF proxies (SPY/DIA/
    // VIXY -- I:SPX/I:DJI/I:VIX return 403, not entitled on the current Polygon
    // plan), Nasdaq via the real I:NDX index endpoint. See indexesExtraData
    // prop below and useIndexesExtraData in Usemarketoverviewdata.js.
    'S&P 500':            { seeds:[8,52,91],  vol:1.6, drift:0.5,  color:PALETTE[4] },
    'Nasdaq':             { seeds:[71,3,64],  vol:2.0, drift:0.62, color:PALETTE[5] },
    'Dow Jones':          { seeds:[26,49,17], vol:1.4, drift:0.4,  color:PALETTE[6] },
    'Volatility (VIX)':   { seeds:[95,6,38],  vol:6.0, drift:-0.1, color:PALETTE[7] },
  },
  // Equities tab is wired (US/EU/Emerging equities -> SPY/VGK/EEM via
  // PROXY_MAP in Usemarketoverviewdata.js). This structure is now only the
  // mock fallback for a total fetch failure.
  'Equities': {
    'US equities':       { seeds:[42,13,77], vol:2.8, drift:0.55, color:PALETTE[0] },
    'EU equities':       { seeds:[19,88,31], vol:2.5, drift:0.38, color:PALETTE[1] },
    'Emerging equities': { seeds:[67,44,22], vol:3.8, drift:0.28, color:PALETTE[2] },
  },
  // ETFs tab is wired (Equity/Bond/Commodity/Mixed -> VTI/BND/GLD/AOR via
  // PROXY_MAP). This structure is now only the mock fallback.
  'ETFs': {
    'Equity ETFs':    { seeds:[31,87,54], vol:2.6, drift:0.50, color:PALETTE[0] },
    'Bond ETFs':      { seeds:[64,21,79], vol:0.9, drift:0.11, color:PALETTE[3] },
    'Commodity ETFs': { seeds:[48,73,16], vol:2.4, drift:0.22, color:PALETTE[2] },
    'Mixed ETFs':     { seeds:[27,59,42], vol:1.6, drift:0.32, color:PALETTE[1] },
  },
  // Crypto tab is wired (see PROXY_MAP in Usemarketoverviewdata.js: Bitcoin/
  // Ethereum/Altcoins -> BTCUSD/ETHUSD/SOLUSD). 'DeFi' and 'Stablecoins' were
  // removed here 2026-08-31 (not just left as mock) -- there's no single
  // representative ticker worth faking for either, per that file's own
  // comment, so they can never graduate out of mock. Keeping them in
  // DEFAULT_STRUCTURE meant they'd still flash real-looking charts during
  // the loading window / on a total fetch failure, since subKeys/seriesMap
  // only fall back to this full mock structure when mergedData is entirely
  // absent -- removing them here is the only way to guarantee they never
  // render, not just usually don't.
  'Crypto': {
    'Bitcoin':     { seeds:[71,44,19], vol:11,  drift:1.1,  color:PALETTE[2] },
    'Ethereum':    { seeds:[38,86,52], vol:13,  drift:0.9,  color:PALETTE[3] },
    'Altcoins':    { seeds:[24,67,93], vol:16,  drift:0.7,  color:PALETTE[1] },
  },
  // Gold tab is wired (Spot gold/Gold miners/Gold ETFs -> XAUUSD/GDX/GLD).
  // 'Gold futures' removed 2026-08-31 -- not accessible on the current
  // Polygon plan tier, same reasoning as Crypto above.
  'Gold': {
    'Spot gold':    { seeds:[91,34,67], vol:1.8, drift:0.44, color:PALETTE[2] },
    'Gold miners':  { seeds:[76,12,53], vol:3.8, drift:0.28, color:PALETTE[1] },
    'Gold ETFs':    { seeds:[29,65,41], vol:1.7, drift:0.43, color:PALETTE[7] },
  },
  // Bonds tab is wired (US/EU/Emerging bonds -> TLT/IBND/VWOB). 'Private
  // debt' removed 2026-08-31 -- no public real-time feed exists, same
  // reasoning as Crypto/Gold above.
  'Bonds': {
    'US bonds':       { seeds:[11,55,93], vol:0.9, drift:0.12, color:PALETTE[0] },
    'EU bonds':       { seeds:[38,72,16], vol:0.8, drift:0.09, color:PALETTE[1] },
    'Emerging bonds': { seeds:[84,29,61], vol:1.4, drift:0.15, color:PALETTE[2] },
  },
};

// Display-only disclosure for the 3 Indexes sub-tabs backed by an ETF proxy
// rather than the real index (I:SPX/I:DJI/I:VIX return 403 on this Polygon
// plan -- see the comment above DEFAULT_STRUCTURE.Indexes). Deliberately a
// separate render-layer lookup, not a rename of the actual keys above --
// those keys are also used for data-matching across useIndexesExtraData,
// liveIndexesData, and seriesMap, so renaming them would risk breaking that
// merge-by-key pipeline for a label-only change. Showing an ETF's price with
// no indication it's a proxy for the index would otherwise be exactly the
// kind of unlabeled substitution this product's own credibility positioning
// argues against.
const INDEXES_PROXY_DISCLOSURE = { 'S&P 500': 'SPY', 'Dow Jones': 'DIA', 'Volatility (VIX)': 'VIXY' };
// Takes `t` as a param rather than being a hook itself so it can be called
// from any subcomponent (Legend, ChartTooltip, D3Chart) without each one
// needing its own useTranslation() call just for this. The label keys here
// are also used as data-matching lookup keys across seriesMap/mergedData/
// structure -- translating only the DISPLAY string, never renaming the key
// itself, is deliberate (see DEFAULT_STRUCTURE comment above).
const displayIndexLabel = (t, label) => {
  const translated = t(`dashboard.marketOverviewD3.labels.${label}`, { defaultValue: label });
  return INDEXES_PROXY_DISCLOSURE[label] ? `${translated} · ${INDEXES_PROXY_DISCLOSURE[label]}` : translated;
};

const RANGES = [
  { label:'1M', months:1 },
  { label:'3M', months:3 },
  { label:'6M', months:6 },
  { label:'1Y', months:12 },
  { label:'3Y', months:36 },
  { label:'5Y', months:60 },
];

const MARGIN = { top:16, right:52, bottom:32, left:4 };

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function computeMetrics(series, rangeLabel) {
  if (!series?.length) return {};
  // A data gap (null/undefined val -- see D3Chart's .defined() accessor)
  // poisons Math.max/arithmetic silently: Math.max(..., undefined, ...) is
  // always NaN regardless of the other values, and dividing by a null/zero
  // `first` is NaN/Infinity -- both render as literal "NaN%"/"Infinity%"
  // text in MetricCards instead of throwing, so this needs its own defined
  // check rather than relying on a crash to surface the problem.
  const defined = (v) => v != null && !Number.isNaN(Number(v));
  const vals = series.map(d => d.val).filter(defined);
  if (!vals.length) return { rangeLabel };
  const first = vals[0], last = vals[vals.length - 1];
  const totalRet = first ? (((last - first) / first) * 100).toFixed(1) : null;
  const max = Math.max(...vals).toFixed(1);
  const deltas = vals.map((v, i) => (i === 0 || !vals[i-1]) ? 0 : (v - vals[i-1]) / vals[i-1] * 100);
  const vol = d3.deviation(deltas.slice(1))?.toFixed(2) ?? '—';
  return {
    last: last.toFixed(2), totalRet, max, vol, rangeLabel,
    up: totalRet != null && parseFloat(totalRet) >= 0,
  };
}

// ─── [1] LIVE DATA TRANSFORMER ───────────────────────────────────────────────
// Converts marketSnapshot arrays (topMovers, topGainers, topLosers, mostActive)
// into the { date, val } time-series format the chart expects.
//
// The snapshot items don't carry full history — they're point-in-time.
// So we synthesise a pseudo-series: the stock's changePercent is used to
// back-calculate a 30-day indexed walk, giving the chart something meaningful
// to visualise rather than a flat line.
//
// Shape in:  [{ ticker, last_price, change_percent, ... }]
// Shape out: [{ date: Date, val: number }]  (30 daily steps indexed to 100)
//
// TODO [G]: Replace this synthetic series with real OHLCV history once
//           the Polygon aggregates proxy endpoint is available.
//           Use stockDataAPI.getSimpleStockRange(ticker, 30) per symbol,
//           normalise each close to 100 at t=0, then average across all
//           symbols in the group for a composite line.
// A single ticker with a corrupted/stale change_percent (e.g. a near-zero
// prevClose producing a nonsensical four-figure "change") can otherwise
// dominate the unweighted average below. Real single-day moves, even
// genuinely dramatic ones for a "Top Movers" group, essentially never
// exceed this in practice -- clamping bounds the damage a single bad data
// point can do without suppressing real volatility this section exists to
// show.
const MAX_PLAUSIBLE_DAILY_CHANGE_PCT = 50;

function buildIndexSeriesFromSnapshots(snapshots = [], months = 1) {
  if (!snapshots.length) return [];
  // Average change_percent across the group as a proxy for category
  // performance. Confirmed live 2026-08-11: without the clamp, a single
  // outlier ticker (~+2000%+ change_percent, a data artifact not a real
  // move) skewed avgChange to ~+211%, which -- because it becomes a
  // *uniform* daily drift applied identically across all ~360 steps of the
  // synthetic walk below -- collapsed the whole "Top Movers" line into a
  // near-straight diagonal, visually distinct from every other (correctly
  // noisy) series sharing this same code path.
  const avgChange = snapshots.reduce((sum, s) => {
    const raw = s.change_percent ?? s.changePercent ?? s.day?.p ?? 0;
    const pct = Math.max(-MAX_PLAUSIBLE_DAILY_CHANGE_PCT, Math.min(MAX_PLAUSIBLE_DAILY_CHANGE_PCT, raw));
    return sum + pct;
  }, 0) / snapshots.length;

  // Build a synthetic indexed series going back `months` months
  const now = new Date();
  const totalDays = Math.max(months * 30, 30);
  const pts = [];
  let val = 100;
  // Distribute the total change evenly with slight noise for visual interest
  const dailyDrift = avgChange / totalDays;
  const seed = Math.abs(Math.round(avgChange * 100)) || 42;
  const rng = lcg(seed);

  for (let i = totalDays; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    // Small noise ± 0.3% per day around the drift
    val = Math.max(5, val + dailyDrift + (rng() - 0.5) * 0.6);
    pts.push({ date: d, val: parseFloat(val.toFixed(2)) });
  }
  return pts;
}

// ─── METRIC CARDS ────────────────────────────────────────────────────────────
const MetricCards = ({ metrics }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const C = theme === 'light' ? LIGHT_C : DARK_C;
  // subClass values were *-600 shades (calibrated for dark text on a light
  // background) sitting on this card's always-dark inline-styled background
  // (C.bgCard, independent of the site's light/dark theme toggle) --
  // insufficient contrast, caught by axe-core's WCAG AA check. Brightened to
  // *-400 to match valueClass's already-correct light-on-dark shades.
  // 2026-09-11: now that C.bgCard actually goes light in light mode, these
  // need a real light-mode pair too, not just the dark-tuned -400 shades.
  const cards = [
    {
      label: t('dashboard.marketOverviewD3.metrics.current'),
      value: metrics.last ?? '—',
      sub: t('dashboard.marketOverviewD3.metrics.indexedTo100'),
      valueClass: 'text-cream-900 dark:text-slate-100',
      subClass: 'text-cream-700 dark:text-slate-400',
    },
    {
      label: t('dashboard.marketOverviewD3.metrics.totalReturn'),
      value: metrics.totalRet != null ? `${metrics.up ? '+' : ''}${metrics.totalRet}%` : '—',
      sub: t('dashboard.marketOverviewD3.metrics.periodSuffix', { range: metrics.rangeLabel ?? '' }),
      valueClass: metrics.up ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400',
      subClass: metrics.up ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400',
    },
    {
      label: t('dashboard.marketOverviewD3.metrics.periodHigh'),
      value: metrics.max ?? '—',
      sub: t('dashboard.marketOverviewD3.metrics.peak'),
      valueClass: 'text-cream-900 dark:text-slate-100',
      subClass: 'text-emerald-700 dark:text-emerald-400',
    },
    {
      label: t('dashboard.marketOverviewD3.metrics.volatility'),
      value: metrics.vol != null ? `${metrics.vol}%` : '—',
      sub: t('dashboard.marketOverviewD3.metrics.monthlySigma'),
      valueClass: 'text-cream-900 dark:text-slate-100',
      subClass: 'text-cream-700 dark:text-slate-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
      {cards.map(c => (
        <div
          key={c.label}
          style={{ background: C.bgCard, borderColor: C.border }}
          className="rounded-lg border px-3 py-2.5"
        >
          {/* Was C.textFaint (#475569) -- ~1.93:1 against this card's
              always-dark background, fails WCAG AA. C.textMuted reused
              instead of touching the shared C.textFaint constant, which is
              also used for chart axis labels elsewhere in this file. */}
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.textMuted }}>
            {c.label}
          </div>
          <div className={`text-sm sm:text-base font-semibold tabular-nums ${c.valueClass}`}>
            {c.value}
          </div>
          <div className={`text-[10px] mt-0.5 ${c.subClass}`}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ─── D3 CHART ────────────────────────────────────────────────────────────────
const D3Chart = ({ seriesMap, activeSubKey, hiddenSet, structure, onHoverDate, chartHeight }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const C = theme === 'light' ? LIGHT_C : DARK_C;
  const svgRef       = useRef(null);
  const containerRef = useRef(null);

  const drawChart = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const W  = container.offsetWidth;
    const H  = chartHeight || 260;
    const cW = W - MARGIN.left - MARGIN.right;
    const cH = H - MARGIN.top - MARGIN.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', W).attr('height', H);

    const defs = svg.append('defs');

    Object.entries(seriesMap).forEach(([k]) => {
      if (hiddenSet.has(k)) return;
      const cfg = structure[k];
      if (!cfg) return;
      const grad = defs.append('linearGradient')
        .attr('id', `mog-${k.replace(/\s+/g, '_')}`)
        .attr('x1','0').attr('y1','0').attr('x2','0').attr('y2','1');
      grad.append('stop').attr('offset','0%')
        .attr('stop-color', cfg.color)
        .attr('stop-opacity', k === activeSubKey ? 0.18 : 0.03);
      grad.append('stop').attr('offset','100%')
        .attr('stop-color', cfg.color).attr('stop-opacity', 0);
    });

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const visEntries = Object.entries(seriesMap).filter(([k]) => !hiddenSet.has(k));
    // Was Object.values(seriesMap)[0] -- only the first series in insertion
    // order set the x-domain, so any other series with a wider/different
    // date range (a real possibility once categories have independently-
    // sourced series, not just same-length mock data) would fall outside
    // the domain and render clipped or missing entirely.
    const allDates  = visEntries.flatMap(([, s]) => s.map(d => d.date));
    const allVals   = visEntries.flatMap(([, s]) => s.map(d => d.val)).filter(v => v != null && !Number.isNaN(v));

    if (!allVals.length || !allDates.length) return;

    const xScale = d3.scaleTime().domain(d3.extent(allDates)).range([0, cW]);
    const [vMin, vMax] = d3.extent(allVals);
    const pad    = (vMax - vMin) * 0.1;
    const yScale = d3.scaleLinear().domain([vMin - pad, vMax + pad]).range([cH, 0]);

    g.append('g').selectAll('line').data(yScale.ticks(4)).join('line')
      .attr('x1', 0).attr('x2', cW)
      .attr('y1', d => yScale(d)).attr('y2', d => yScale(d))
      .attr('stroke', C.gridLine).attr('stroke-width', 1);

    const isMobile  = W < 400;
    const tickFmt   = allDates.length > 200 ? d3.timeFormat('%Y') : d3.timeFormat('%b %y');
    g.append('g').attr('transform', `translate(0,${cH})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(isMobile ? 3 : 5)
          .tickSize(0)
          .tickPadding(10)
          .tickFormat(tickFmt)
      )
      .call(a => {
        a.select('.domain').remove();
        a.selectAll('text').attr('fill', C.textFaint).attr('font-size', isMobile ? 9 : 10);
      });

    g.append('g').attr('transform', `translate(${cW},0)`)
      .call(
        d3.axisRight(yScale)
          .ticks(4)
          .tickSize(0)
          .tickPadding(6)
          .tickFormat(d => d.toFixed(0))
      )
      .call(a => {
        a.select('.domain').remove();
        a.selectAll('text').attr('fill', C.textFaint).attr('font-size', isMobile ? 9 : 10);
      });

    // Without .defined(), a null/undefined val (a real data gap, not
    // necessarily an error) either coerces to 0 (null) or breaks the path
    // at that point on with NaN (undefined) -- both draw a misleading solid
    // line straight through the gap instead of visibly breaking it there.
    const isDefined = (d) => d.val != null && !Number.isNaN(d.val);
    const line = d3.line()
      .defined(isDefined)
      .x(d => xScale(d.date)).y(d => yScale(d.val))
      .curve(d3.curveCatmullRom.alpha(0.5));
    const area = d3.area()
      .defined(isDefined)
      .x(d => xScale(d.date)).y0(cH).y1(d => yScale(d.val))
      .curve(d3.curveCatmullRom.alpha(0.5));

    visEntries.forEach(([k, series]) => {
      const cfg     = structure[k];
      if (!cfg) return;
      const isFocus = k === activeSubKey;

      if (isFocus) {
        g.append('path').datum(series)
          .attr('fill', `url(#mog-${k.replace(/\s+/g, '_')})`)
          .attr('d', area);
      }

      const path = g.append('path').datum(series)
        .attr('fill', 'none')
        .attr('stroke', cfg.color)
        .attr('stroke-width', isFocus ? 2 : 1)
        .attr('stroke-opacity', isFocus ? 1 : 0.22)
        .attr('d', line);

      const len = path.node().getTotalLength();
      path
        .attr('stroke-dasharray', `${len} ${len}`)
        .attr('stroke-dashoffset', len)
        .transition().duration(700).ease(d3.easeQuadOut)
        .attr('stroke-dashoffset', 0);

      // Was series[series.length - 1] unconditionally -- if the series'
      // actual last point is a gap, last.val.toFixed(1) below throws
      // (null/undefined has no .toFixed), not just mispositions the marker.
      const last = [...series].reverse().find(isDefined);
      if (isFocus && last) {
        const lx   = xScale(last.date);
        const ly   = yScale(last.val);

        g.append('circle')
          .attr('cx', lx).attr('cy', ly).attr('r', 3.5)
          .attr('fill', cfg.color)
          .attr('stroke', C.bg).attr('stroke-width', 1.5)
          .attr('opacity', 0)
          .transition().delay(680).duration(160).attr('opacity', 1);

        g.append('text')
          .attr('x', lx + 7).attr('y', ly + 4)
          .attr('fill', cfg.color).attr('font-size', 10).attr('font-weight', '600')
          .text(last.val.toFixed(1))
          .attr('opacity', 0)
          .transition().delay(680).duration(160).attr('opacity', 1);
      }
    });

    const crossV = g.append('line').attr('y1', 0).attr('y2', cH)
      .attr('stroke', C.crosshair).attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3').attr('opacity', 0);
    const crossH = g.append('line').attr('x1', 0).attr('x2', cW)
      .attr('stroke', C.crosshair).attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3').attr('opacity', 0);
    const dot = g.append('circle').attr('r', 4)
      .attr('fill', C.bg)
      .attr('stroke', structure[activeSubKey]?.color ?? PALETTE[0])
      .attr('stroke-width', 2).attr('opacity', 0);

    const bisect = d3.bisector(d => d.date).left;

    const handlePointer = (event) => {
      const [mx] = d3.pointer(event);
      const date = xScale.invert(mx);
      const focusSeries = seriesMap[activeSubKey];
      if (!focusSeries) return;
      const idx = bisect(focusSeries, date, 1);
      const a   = focusSeries[idx - 1], b = focusSeries[idx];
      const pt  = !a ? b : !b ? a : (date - a.date > b.date - date ? b : a);
      if (!pt) return;
      const cx = xScale(pt.date), cy = yScale(pt.val);
      crossV.attr('x1', cx).attr('x2', cx).attr('opacity', 1);
      crossH.attr('y1', cy).attr('y2', cy).attr('opacity', 1);
      dot.attr('cx', cx).attr('cy', cy).attr('opacity', 1);

      const rows = visEntries.map(([k, s]) => {
        const i   = bisect(s, pt.date, 1);
        const p   = s[Math.min(i, s.length - 1)];
        const clr = structure[k]?.color ?? '#888';
        return { key: k, val: p?.val.toFixed(1) ?? '—', color: clr, active: k === activeSubKey };
      });
      onHoverDate?.({ date: pt.date, rows, svgX: MARGIN.left + cx, svgY: MARGIN.top + cy, containerW: W });
    };

    g.append('rect')
      .attr('width', cW).attr('height', cH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', handlePointer)
      .on('touchmove', (event) => {
        event.preventDefault();
        handlePointer(event.touches[0]);
      })
      .on('mouseleave touchend', () => {
        crossV.attr('opacity', 0);
        crossH.attr('opacity', 0);
        dot.attr('opacity', 0);
        onHoverDate?.(null);
      });

  }, [seriesMap, activeSubKey, hiddenSet, structure, onHoverDate, chartHeight, C]);

  useEffect(() => { drawChart(); }, [drawChart]);

  useEffect(() => {
    const obs = new ResizeObserver(drawChart);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [drawChart]);

  return (
    <div ref={containerRef} className="w-full h-full">
      <svg
        ref={svgRef}
        style={{ display: 'block' }}
        aria-label={t('dashboard.marketOverviewD3.chartAriaLabel')}
        role="img"
      />
    </div>
  );
};

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
const ChartTooltip = ({ hover }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const C = theme === 'light' ? LIGHT_C : DARK_C;
  if (!hover) return null;
  const { date, rows, svgX, svgY, containerW } = hover;
  const ttW  = 168;
  let   left = svgX + 14;
  if (left + ttW > containerW - 8) left = svgX - ttW - 14;
  const top = Math.max(8, svgY - 28);

  return (
    <div
      style={{
        position: 'absolute', left, top, width: ttW,
        pointerEvents: 'none', zIndex: 20,
        background: C.bgCard,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '8px 12px',
      }}
    >
      <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 6 }}>
        {d3.timeFormat('%b %Y')(new Date(date))}
      </div>
      {rows.map(r => (
        <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingBottom: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
            <span style={{
              fontSize: 10,
              color: r.active ? C.textPrimary : C.textFaint,
              fontWeight: r.active ? 600 : 400,
            }}>
              {displayIndexLabel(t, r.key)}
            </span>
          </span>
          <span style={{ fontSize: 10, color: r.active ? C.textPrimary : C.textFaint, fontWeight: r.active ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>
            {r.val}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── LEGEND ──────────────────────────────────────────────────────────────────
const Legend = ({ seriesMap, structure, activeSubKey, hiddenSet, onToggle }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const C = theme === 'light' ? LIGHT_C : DARK_C;
  return (
  <div
    style={{ borderTop: `1px solid ${C.border}` }}
    className="flex flex-wrap gap-1 px-3 py-2.5"
  >
    {Object.entries(seriesMap).map(([k, s]) => {
      const cfg    = structure[k];
      if (!cfg) return null;
      const first  = s[0]?.val, last = s[s.length - 1]?.val;
      const ret    = first ? ((last - first) / first * 100).toFixed(1) : '—';
      const up     = parseFloat(ret) >= 0;
      const hidden = hiddenSet.has(k);
      const isActive = k === activeSubKey;

      return (
        <button
          key={k}
          onClick={() => onToggle(k)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 8px', borderRadius: 6,
            background: isActive ? C.bgCardHover : 'transparent',
            opacity: hidden ? 0.3 : 1,
            border: isActive ? `1px solid ${C.border}` : '1px solid transparent',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          <span style={{ width: 12, height: 2, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: isActive ? C.textPrimary : C.textMuted, fontWeight: isActive ? 600 : 400 }}>
            {displayIndexLabel(t, k)}
          </span>
          <span style={{ fontSize: 10, color: up ? C.green : C.red, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {up ? '+' : ''}{ret}%
          </span>
        </button>
      );
    })}
  </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const MarketOverviewD3 = ({
  data: externalData = null,   // Pass null to use internal live data + mock fallback
  isLoading: externalLoading = false,
  indexesExtraData = null,     // #14: S&P 500/Dow Jones/Volatility (VIX)/Nasdaq, merged into the real Indexes tab below
  onSubChange,
  className = '',
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const C = theme === 'light' ? LIGHT_C : DARK_C;

  // ── [1] WIRED: Pull live market snapshot data from Redux ──────────────────
  // fetchMarketOverview() is dispatched from UnifiedStockerDashboard (Effect 1 or 2).
  // This component only reads — it does not re-dispatch.
  //
  // If this component is used standalone (outside the dashboard), add:
  //   useEffect(() => { dispatch(fetchMarketOverview()); }, [dispatch]);
  // and remove the dependency on the parent dispatching it.
  const {
    topMovers  = [],
    topGainers = [],
    topLosers  = [],
    mostActive = [],
    status: marketSnapshotStatus,
  } = useSelector((s) => s.marketSnapshot || {});

  // TODO [A]: Pull nasdaqIndex state when wired:
  // const nasdaqIndexState = useSelector((s) => s.indices || {});
  // const nasdaqPrices     = nasdaqIndexState.indexPrices || {};

  const snapshotLoading = marketSnapshotStatus === 'loading';
  const isLoading = externalLoading || snapshotLoading;

  const cats = Object.keys(DEFAULT_STRUCTURE);
  const [activeT1,    setActiveT1]    = useState(cats[0]);
  const [activeT2,    setActiveT2]    = useState(Object.keys(DEFAULT_STRUCTURE[cats[0]])[0]);
  const [activeRange, setActiveRange] = useState(3); // default 1Y
  const [hiddenSet,   setHiddenSet]   = useState(new Set());
  const [hover,       setHover]       = useState(null);

  const [chartHeight, setChartHeight] = useState(260);
  useEffect(() => {
    const update = () => setChartHeight(window.innerWidth < 640 ? 200 : 260);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // TODO [G]: When user changes range, fire a new fetch instead of slicing mock data.
  // Pattern:
  //   const handleRangeChange = (i) => {
  //     setActiveRange(i);
  //     const { months } = RANGES[i];
  //     const end = new Date();
  //     const start = new Date(); start.setMonth(start.getMonth() - months);
  //     if (activeT1 === 'Indexes') {
  //       // fetchMarketOverview already gives point-in-time data, not history.
  //       // You'll need a different call per sub-key for history.
  //       // dispatch(fetchIndexPrices({ ticker: tickerFor(activeT2), limit: months * 30 }));
  //     }
  //   };

  // ── [1] Build live Indexes data from market snapshot arrays ──────────────
  //
  // Shape produced: { 'Top Movers': [{date,val}], 'Top Gainers': [...], ... }
  // This is passed as the 'Indexes' key in liveData below.
  //
  // Each sub-series is a synthetic indexed walk derived from the group's average
  // change_percent. See buildIndexSeriesFromSnapshots() above for the approach
  // and TODO [G] for upgrading to real historical series.
  const liveIndexesData = useMemo(() => {
    const months = RANGES[activeRange].months;
    return {
      'Top Movers':  buildIndexSeriesFromSnapshots(topMovers,  months),
      'Top Gainers': buildIndexSeriesFromSnapshots(topGainers, months),
      'Top Losers':  buildIndexSeriesFromSnapshots(topLosers,  months),
      'Most Active': buildIndexSeriesFromSnapshots(mostActive, months),
      // #14: real S&P 500/Dow Jones/Volatility/Nasdaq series, already
      // normalised to [{date, val}] by useIndexesExtraData. Only added when
      // non-empty so an in-flight/failed fetch falls through to mock per
      // sub-tab (matching every other live category's fallback behavior)
      // instead of rendering 4 empty charts.
      ...(indexesExtraData || {}),
    };
  }, [topMovers, topGainers, topLosers, mostActive, activeRange, indexesExtraData]);

  // ── Merged data object ───────────────────────────────────────────────────
  // 'Indexes' always comes from this component's own marketSnapshot read
  // (topMovers/Gainers/Losers/MostActive) -- externalData SUPPLEMENTS the
  // other categories (Equities/ETFs/Bonds/Gold/Crypto), it does not replace
  // Indexes. This used to be `if (externalData) return externalData`, a full
  // override that would have silently killed the one already-live tab the
  // moment a parent started passing real data for the other 5 categories.
  const mergedData = useMemo(() => {
    // #14: also true when the S&P 500/Dow/VIX/Nasdaq proxies have real data,
    // even if marketSnapshot's Top Movers/etc. are stale/empty (a separate,
    // already-tracked staleness issue) -- otherwise a stale market_snapshots
    // table would incorrectly hide real proxy data behind full mock too.
    const hasLiveIndexes = topMovers.length || topGainers.length ||
                           topLosers.length || mostActive.length ||
                           Object.keys(indexesExtraData || {}).length;
    const indexesPart = hasLiveIndexes ? { Indexes: liveIndexesData } : {};

    const merged = { ...indexesPart, ...(externalData || {}) };
    return Object.keys(merged).length ? merged : null; // all-empty → full mock fallback
  }, [externalData, liveIndexesData, topMovers, topGainers, topLosers, mostActive, indexesExtraData]);

  // ── Series map (chart input) ─────────────────────────────────────────────
  const seriesMap = useMemo(() => {
    const months = RANGES[activeRange].months;

    // Live data path
    if (mergedData?.[activeT1]) {
      const result = {};
      Object.entries(mergedData[activeT1]).forEach(([k, pts]) => {
        if (!pts?.length) return;
        result[k] = pts.map(p => ({
          date: p.date instanceof Date ? p.date : new Date(p.date),
          val:  p.val,
        }));
      });
      // If result is empty (API returned nothing for this sub), fall through to mock
      if (Object.keys(result).length) return result;
    }

    // Mock fallback path — always works, safe for unwired categories
    const struct = DEFAULT_STRUCTURE[activeT1] ?? {};
    const result = {};
    Object.entries(struct).forEach(([k, cfg]) => {
      result[k] = generateSeries(cfg.seeds, cfg.vol, cfg.drift, months);
    });
    return result;
  }, [mergedData, activeT1, activeRange]);

  const structure = useMemo(() => {
    // When live data is active for this category, assign colors by position
    if (mergedData?.[activeT1]) {
      const subs = Object.keys(mergedData[activeT1] ?? {});
      return Object.fromEntries(
        subs.map((k, i) => [k, { color: PALETTE[i % PALETTE.length] }])
      );
    }
    return DEFAULT_STRUCTURE[activeT1] ?? {};
  }, [mergedData, activeT1]);

  const metrics = useMemo(
    () => computeMetrics(seriesMap[activeT2], RANGES[activeRange].label),
    [seriesMap, activeT2, activeRange]
  );

  // activeT2's initial value is a synchronous guess (first key of
  // DEFAULT_STRUCTURE) made before live data has loaded. For Indexes, that
  // guess is 'Top Movers' -- but the dashboard never dispatches the
  // fetchTopMovers/fetchTopGainers/fetchTopLosers/fetchMostActive thunks, so
  // that sub never gets data and seriesMap ends up with no 'Top Movers' key
  // at all once the real S&P 500/Dow/VIX/Nasdaq series load (seriesMap skips
  // empty subs, see the `if (!pts?.length) return;` above). activeT2 was
  // left pointing at a key seriesMap doesn't have, so computeMetrics got
  // `undefined` and every stat tile silently rendered "--" even though the
  // chart itself had real data to show. Mirrors switchT1's existing
  // skip-empty-subs logic, just applied reactively instead of only on click.
  useEffect(() => {
    const keys = Object.keys(seriesMap);
    if (keys.length && !seriesMap[activeT2]) {
      setActiveT2(keys[0]);
    }
  }, [seriesMap, activeT2]);

  const switchT1 = useCallback((cat) => {
    // Skip subs with no data points -- selecting one leaves activeT2 pointing
    // at a key seriesMap never builds an entry for (seriesMap itself already
    // drops empty subs), which killed the hover tooltip entirely (D3Chart's
    // handlePointer bails out with no focusSeries) rather than just that
    // sub's own display.
    const firstSub = mergedData
      ? Object.entries(mergedData[cat] ?? {}).find(([, pts]) => pts?.length)?.[0]
      : Object.keys(DEFAULT_STRUCTURE[cat] ?? {})[0];
    setActiveT1(cat);
    setActiveT2(firstSub ?? '');
    setHiddenSet(new Set());
    onSubChange?.(cat, firstSub);

    // TODO [A–F]: Dispatch data-fetching thunks here when user switches tabs.
    // Pattern:
    //   if (cat === 'Indexes') {
    //     dispatch(fetchIndexPrices({ ticker: 'COMP.INDX', limit: 365 }));
    //   } else if (cat === 'Equities') {
    //     dispatch(fetchEquitiesSeriesThunk({ tickers: ['SPY', 'VGK', 'EEM'] }));
    //   }
  }, [mergedData, onSubChange]);

  const switchT2 = useCallback((sub) => {
    setActiveT2(sub);
    setHiddenSet(new Set());
    onSubChange?.(activeT1, sub);
  }, [activeT1, onSubChange]);

  const toggleSeries = useCallback((k) => {
    setHiddenSet(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }, []);

  // Same reasoning as switchT1 above: only list subs that seriesMap actually
  // builds an entry for, so a pill can never be selected into a dead state.
  const subKeys = mergedData?.[activeT1]
    ? Object.entries(mergedData[activeT1]).filter(([, pts]) => pts?.length).map(([k]) => k)
    : Object.keys(DEFAULT_STRUCTURE[activeT1] ?? {});

  // Determine which categories have live data (for badge rendering)
  const liveCategories = new Set(mergedData ? Object.keys(mergedData) : []);

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex gap-1">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-7 rounded-md animate-pulse" style={{ width: 60, background: C.bgCard }} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: C.bgCard }} />
          ))}
        </div>
        <div className="rounded-xl animate-pulse" style={{ height: chartHeight + 60, background: C.bgCard }} />
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <section className={className} aria-label={t('dashboard.marketOverviewD3.ariaLabel')}>

      {/* ── TIER 1 — category tabs ────────────────────────────────────────── */}
      <div
        className="flex overflow-x-auto scrollbar-none mb-0"
        style={{ borderBottom: `1px solid ${C.border}`, gap: 0 }}
      >
        {cats.map(cat => {
          const isActive = activeT1 === cat;
          const isLive   = liveCategories.has(cat);
          return (
            <button
              key={cat}
              onClick={() => switchT1(cat)}
              style={{
                padding: '7px 12px',
                fontSize: 12,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? C.textPrimary : C.textMuted,
                borderBottom: isActive ? `2px solid ${C.blue}` : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 0.15s',
                position: 'relative',
              }}
            >
              {displayIndexLabel(t, cat)}
              {/* Data-source dot -- green for real data, amber for mock (#11:
                  previously mock categories got no indicator at all here,
                  the opposite direction of the same-issue fixed above). */}
              <span
                style={{
                  display: 'inline-block',
                  width: 5, height: 5,
                  borderRadius: '50%',
                  background: isLive ? C.green : C.amber,
                  marginLeft: 4,
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: -1,
                }}
                title={isLive ? t('dashboard.marketOverviewD3.liveDataTitle') : t('dashboard.marketOverviewD3.mockDataTitle')}
              />
            </button>
          );
        })}
      </div>

      {/* ── TIER 2 — subcategory pills ────────────────────────────────────── */}
      <div className="flex overflow-x-auto scrollbar-none gap-1.5 py-2.5">
        {subKeys.map(sub => {
          const isActive = activeT2 === sub;
          return (
            <button
              key={sub}
              onClick={() => switchT2(sub)}
              style={{
                padding: '4px 11px',
                fontSize: 11,
                fontWeight: isActive ? 600 : 400,
                // Was C.textPrimary (#f1f5f9) on C.blue (#3b82f6) -- ~3.35:1,
                // fails WCAG AA for this small (11px) text. Darkened just
                // this pill's active background (not the shared C.blue
                // constant, which is also used as a border accent
                // elsewhere) so white text clears 4.5:1.
                color: isActive ? '#ffffff' : C.textMuted,
                background: isActive ? '#1d4ed8' : C.bgCard,
                border: `1px solid ${isActive ? '#1d4ed8' : C.border}`,
                borderRadius: 20,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}
            >
              {displayIndexLabel(t, sub)}
            </button>
          );
        })}
      </div>

      {/* ── METRIC CARDS ──────────────────────────────────────────────────── */}
      <MetricCards metrics={metrics} />

      {/* ── CHART SHELL ───────────────────────────────────────────────────── */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{ background: C.bgCard, border: `1px solid ${C.border}` }}
      >
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
          <span style={{ fontSize: 11, color: C.textFaint, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
            {displayIndexLabel(t, activeT2)}
            {/* Data source label -- MOCK now carries the same visual weight
                as LIVE (a colored dot + colored, same-weight text) instead
                of fading into the background at 9px faint gray, so a viewer
                actually notices when a sub-tab isn't real data (#11). */}
            {liveCategories.has(activeT1)
              ? <span style={{ fontSize: 9, color: C.green, fontWeight: 500, letterSpacing: '0.04em' }}>● {t('dashboard.marketOverviewD3.live')}</span>
              : <span style={{ fontSize: 9, color: C.amber, fontWeight: 500, letterSpacing: '0.04em' }}>● {t('dashboard.marketOverviewD3.mock')}</span>
            }
          </span>
          <div className="flex gap-1">
            {RANGES.map((r, i) => {
              const isActive = activeRange === i;
              return (
                <button
                  key={r.label}
                  onClick={() => setActiveRange(i)}
                  // TODO [G]: Replace setActiveRange(i) with handleRangeChange(i)
                  //           once range changes trigger API re-fetches.
                  style={{
                    padding: '3px 8px',
                    fontSize: 10,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? C.textPrimary : C.textFaint,
                    background: isActive ? C.bgCardHover : 'transparent',
                    border: `1px solid ${isActive ? C.border : 'transparent'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative" style={{ height: chartHeight }}>
          <D3Chart
            seriesMap={seriesMap}
            activeSubKey={activeT2}
            hiddenSet={hiddenSet}
            structure={structure}
            onHoverDate={setHover}
            chartHeight={chartHeight}
          />
          <ChartTooltip hover={hover} />
        </div>

        <Legend
          seriesMap={seriesMap}
          structure={structure}
          activeSubKey={activeT2}
          hiddenSet={hiddenSet}
          onToggle={toggleSeries}
        />
      </div>

    </section>
  );
};

export default MarketOverviewD3;